import { useAppDispatch, useAppSelector } from './useRedux';
import { AuthService } from '../services/auth.service';
import { setCredentials, logout, updateMfaStatus } from '../store/slices/authSlice';
import { clearWorkspaceState } from '../store/slices/workspaceSlice';
import { ILoginCredentials, IRegisterCredentials } from '../types/auth.types';
import { deriveKeys, decryptVaultKey, encryptKeyData } from '../utils/encryption';
import { secureStore } from '../utils/secureStore';
import { getBrowserAPI } from '../utils/browser';
import { config } from '../config';
import { notifyExtensionOfLogin, notifyExtensionOfLogout } from '../utils/extensionSync';

interface IAuthContext {
    user: string;
    isAuthenticated: boolean;
    loading: boolean;
    error: string | null;
    verifyMasterPassword: (password: string) => Promise<boolean>;
    role_id: number | null;
    permissions: {
        manage_team: boolean;
        manage_roles: boolean;
        invite_members: boolean;
        remove_members: boolean;
        manage_categories: boolean;
    } | null;
    team_id: string | null;
}

export const useAuth = () => {
    const dispatch = useAppDispatch();
    const { user, loading, error, isAuthenticated, role_id, permissions, team_id } = useAppSelector(
        (state) => state.auth
    );
    

    // Removed saveAuthToExtension function - now using notifyExtensionOfLogin from extensionSync utility

    const login = async (credentials: ILoginCredentials) => {
        try {
            // If MFA code is provided, verify MFA
            if (credentials.mfaCode && credentials.tempToken) {
                const mfaResponse = await AuthService.verifyMfa({
                    code: credentials.mfaCode,
                    tempToken: credentials.tempToken
                });

                if (mfaResponse.success && mfaResponse.data) {
                    // Derive keys again for MFA verification
                    const { encryptionKey, symmetricKey } = deriveKeys(
                        credentials.password,
                        credentials.email
                    );

                    let vaultKey: string;
                    const cryptoVersion = mfaResponse.data.cryptoVersion || 1;

                    if (cryptoVersion === 1) {
                        // Legacy crypto version 1: AES-only
                        console.log('MFA: Using crypto_version 1 flow (legacy AES encryption)');
                        
                        if (!mfaResponse.data.encryptedVaultKey) {
                            throw new Error('Server response missing encrypted vault key for crypto version 1');
                        }

                        vaultKey = decryptVaultKey(
                            mfaResponse.data.encryptedVaultKey,
                            encryptionKey
                        );
                        
                        console.log('MFA: ✓ Vault key decrypted successfully (AES)');
                    } else if (cryptoVersion === 2) {
                        // New crypto version 2: RSA + AES
                        console.log('MFA: Using crypto_version 2 flow (RSA hybrid encryption)');
                        
                        if (!mfaResponse.data.encryptedPrivateKey || !mfaResponse.data.vaultKeys) {
                            console.error('MFA: Missing RSA keys in response:', {
                                hasEncryptedPrivateKey: !!mfaResponse.data.encryptedPrivateKey,
                                hasVaultKeys: !!mfaResponse.data.vaultKeys
                            });
                            throw new Error('Server response missing RSA keys for crypto version 2');
                        }

                        const { loadPrivateKey, hybridDecrypt } = await import('../utils/rsaCrypto');
                        
                        // Decrypt RSA private key using derived encryption key
                        console.log('MFA: Decrypting RSA private key...');
                        const privateKey = await loadPrivateKey(
                            mfaResponse.data.encryptedPrivateKey,
                            encryptionKey
                        );
                        console.log('MFA: ✓ RSA private key loaded successfully');

                        // Decrypt vault key using RSA private key
                        console.log('MFA: Decrypting vault key with RSA...');
                        vaultKey = await hybridDecrypt(
                            mfaResponse.data.vaultKeys.encryptedData,
                            mfaResponse.data.vaultKeys.encryptedKey,
                            mfaResponse.data.vaultKeys.iv,
                            privateKey,
                            mfaResponse.data.vaultKeys.authTag
                        );
                        console.log('MFA: ✓ Vault key decrypted successfully (RSA)');
                    } else {
                        throw new Error(`Unsupported crypto version: ${cryptoVersion}`);
                    }

                    // Store the keys
                    secureStore.setKeys(
                        encryptionKey,
                        symmetricKey,
                        vaultKey
                    );

                    // Store encrypted private key and authKey for super admin access
                    console.log('=== CHECKING FOR ADMIN KEYS (MFA) ===');
                    console.log('cryptoVersion:', cryptoVersion);
                    console.log('mfaResponse.data keys:', Object.keys(mfaResponse.data));
                    console.log('encryptedPrivateKey exists:', !!mfaResponse.data.encryptedPrivateKey);
                    console.log('encryptedPrivateKey value:', mfaResponse.data.encryptedPrivateKey);
                    
                    if (cryptoVersion === 2 && mfaResponse.data.encryptedPrivateKey) {
                        sessionStorage.setItem('encryptedPrivateKey', JSON.stringify(mfaResponse.data.encryptedPrivateKey));
                        sessionStorage.setItem('authKey', encryptionKey);
                        console.log('✓ Stored encrypted private key and authKey for admin access');
                        console.log('✓ sessionStorage now has:', sessionStorage.getItem('encryptedPrivateKey') ? 'encryptedPrivateKey' : 'NOTHING');
                    } else {
                        console.log('❌ NOT storing keys. Reason:', 
                            cryptoVersion !== 2 ? `crypto version is ${cryptoVersion}` : 'encryptedPrivateKey missing');
                    }

                    // First set the initial auth data (no token with cookie-based auth)
                    let initialAuthData = {
                        user: {
                            ...mfaResponse.data.user,
                            two_factor_enabled: mfaResponse.data.two_factor_enabled || true
                        },
                        isAuthenticated: true,
                        role_id: String(mfaResponse.data.role_id), // Convert to string for compatibility
                        permissions: mfaResponse.data.permissions,
                        team_id: mfaResponse.data.team_id,
                        activeWorkspaceId: mfaResponse.data.activeWorkspaceId || undefined
                    };

                    // Dispatch initial credentials
                    dispatch(setCredentials(initialAuthData));

                    // If there's an active workspace, get the workspace-specific role and permissions
                    if (mfaResponse.data.activeWorkspaceId) {
                        try {
                            // Now we can safely call setActiveWorkspace since token is available
                            const workspaceResponse = await AuthService.setActiveWorkspace(mfaResponse.data.activeWorkspaceId);
                            if (workspaceResponse.success) {
                                // Update auth data with workspace-specific role and permissions
                                const updatedAuthData = {
                                    ...initialAuthData,
                                    role_id: workspaceResponse.role_id,
                                    permissions: workspaceResponse.permissions
                                };
                                dispatch(setCredentials(updatedAuthData));
                                initialAuthData = updatedAuthData; // Update for extension sync
                            }
                        } catch (workspaceError) {
                            console.error('Error setting workspace permissions:', workspaceError);
                            // Continue with login even if workspace permission fetch fails
                        }
                    }

                    window.postMessage(
                        {
                            type: "FROM_WEB_ACTIVE_WORKSPACE",
                            payload: { activeWorkspaceId: mfaResponse.data.activeWorkspaceId },
                        },
                        "*"
                    );

                    if (!mfaResponse.data.activeWorkspaceId) {
                        dispatch(clearWorkspaceState());
                    }
                    
                    dispatch(updateMfaStatus(true));

                    // Notify extension of login with all auth data
                    try {
                        await notifyExtensionOfLogin({
                            isAuthenticated: true,
                            user: initialAuthData.user,
                            role_id: initialAuthData.role_id,
                            permissions: initialAuthData.permissions,
                            team_id: initialAuthData.team_id,
                            workspaceId: mfaResponse.data.activeWorkspaceId,
                            encryptionKey: encryptionKey,
                            symmetricKey: symmetricKey,
                            vaultKey: vaultKey
                        });
                        console.log('Web: Extension notified of MFA login success');
                    } catch (error) {
                        console.error('Failed to sync with extension, but login successful:', error);
                    }

                    // Return success with crypto version info for migration prompt
                    return { 
                        success: true,
                        cryptoVersion: cryptoVersion,
                        showMigrationPrompt: cryptoVersion === 1 // Show prompt for old users
                    };
                }

                return {
                    success: false,
                    error: mfaResponse.message || 'MFA verification failed'
                };
            }

            // Regular login flow
            const { authKey, encryptionKey, symmetricKey } = deriveKeys(
                credentials.password,
                credentials.email
            );

            const response = await AuthService.login({
                email: credentials.email,
                authKey: authKey
            });

            if (response.requiresMfa) {
                return {
                    success: false,
                    requiresMfa: true,
                    tempToken: response.tempToken,
                    message: 'MFA verification required'
                };
            }

            if (response.success && response.data) {
                try {
                    console.log('=== FRONTEND LOGIN RESPONSE ===');
                    console.log('response.data.cryptoVersion:', response.data.cryptoVersion);
                    console.log('response.data keys:', Object.keys(response.data));
                    console.log('has encryptedVaultKey:', !!response.data.encryptedVaultKey);
                    console.log('has encryptedPrivateKey:', !!response.data.encryptedPrivateKey);
                    console.log('has vaultKeys:', !!response.data.vaultKeys);
                    console.log('================================');
                    
                    let vaultKey: string;
                    const cryptoVersion = response.data.cryptoVersion || 1;

                    if (cryptoVersion === 1) {
                        // Legacy crypto version 1: AES-only
                        console.log('Using crypto_version 1 flow (legacy AES encryption)');
                        
                        if (!response.data.encryptedVaultKey) {
                            throw new Error('Server response missing encrypted vault key for crypto version 1');
                        }

                        vaultKey = decryptVaultKey(
                            response.data.encryptedVaultKey,
                            encryptionKey
                        );
                        
                        console.log('✓ Vault key decrypted successfully (AES)');
                    } else if (cryptoVersion === 2) {
                        // New crypto version 2: RSA + AES
                        console.log('Using crypto_version 2 flow (RSA hybrid encryption)');
                        
                        if (!response.data.encryptedPrivateKey || !response.data.vaultKeys) {
                            console.error('Missing RSA keys in response:', {
                                hasEncryptedPrivateKey: !!response.data.encryptedPrivateKey,
                                hasVaultKeys: !!response.data.vaultKeys
                            });
                            throw new Error('Server response missing RSA keys for crypto version 2');
                        }

                        const { loadPrivateKey, hybridDecrypt } = await import('../utils/rsaCrypto');

                        // Decrypt RSA private key using derived encryption key
                        console.log('Decrypting RSA private key...');
                        const privateKey = await loadPrivateKey(
                            response.data.encryptedPrivateKey,
                            encryptionKey
                        );
                        console.log('✓ RSA private key loaded successfully');

                        // Decrypt vault key using RSA private key (hybridDecrypt returns string)
                        console.log('Decrypting vault key with RSA...');
                        vaultKey = await hybridDecrypt(
                            response.data.vaultKeys.encryptedData,
                            response.data.vaultKeys.encryptedKey,
                            response.data.vaultKeys.iv,
                            privateKey,
                            response.data.vaultKeys.authTag
                        );
                        console.log('✓ Vault key decrypted successfully (RSA)');
                    } else {
                        throw new Error(`Unsupported crypto version: ${cryptoVersion}`);
                    }

                    // Store the keys
                    secureStore.setKeys(
                        encryptionKey,
                        symmetricKey,
                        vaultKey
                    );

                    // Store encrypted private key and authKey for super admin access
                    console.log('=== CHECKING FOR ADMIN KEYS ===');
                    console.log('cryptoVersion:', cryptoVersion);
                    console.log('response.data keys:', Object.keys(response.data));
                    console.log('encryptedPrivateKey exists:', !!response.data.encryptedPrivateKey);
                    console.log('encryptedPrivateKey value:', response.data.encryptedPrivateKey);
                    
                    if (cryptoVersion === 2 && response.data.encryptedPrivateKey) {
                        sessionStorage.setItem('encryptedPrivateKey', JSON.stringify(response.data.encryptedPrivateKey));
                        sessionStorage.setItem('authKey', encryptionKey);
                        console.log('✓ Stored encrypted private key and authKey for admin access');
                        console.log('✓ sessionStorage now has:', sessionStorage.getItem('encryptedPrivateKey') ? 'encryptedPrivateKey' : 'NOTHING');
                    } else {
                        console.log('❌ NOT storing keys. Reason:', 
                            cryptoVersion !== 2 ? `crypto version is ${cryptoVersion}` : 'encryptedPrivateKey missing');
                    }

                    // First set the initial auth data (no token with cookie-based auth)
                    let initialAuthData = {
                        user: response.data.user,
                        isAuthenticated: true,
                        role_id: String(response.data.role_id), // Convert to string for compatibility
                        permissions: response.data.permissions,
                        team_id: response.data.team_id,
                        activeWorkspaceId: response.data.activeWorkspaceId || undefined
                    };

                    // Dispatch initial credentials
                    dispatch(setCredentials(initialAuthData));

                    // If there's an active workspace, get the workspace-specific role and permissions
                    if (response.data.activeWorkspaceId) {
                        try {
                            // Call setActiveWorkspace (now uses cookies instead of token)
                            const workspaceResponse = await AuthService.setActiveWorkspace(response.data.activeWorkspaceId);
                            if (workspaceResponse.success) {
                                // Update auth data with workspace-specific role and permissions
                                const updatedAuthData = {
                                    ...initialAuthData,
                                    role_id: workspaceResponse.role_id,
                                    permissions: workspaceResponse.permissions
                                };
                                dispatch(setCredentials(updatedAuthData));
                                initialAuthData = updatedAuthData; // Update for extension sync
                            }
                        } catch (workspaceError) {
                            console.error('Error setting workspace permissions:', workspaceError);
                            // Continue with login even if workspace permission fetch fails
                        }
                    }

                    window.postMessage(
                        {
                            type: "FROM_WEB_ACTIVE_WORKSPACE",
                            payload: { activeWorkspaceId: response.data.activeWorkspaceId },
                        },
                        "*"
                    );

                    if (!response.data.activeWorkspaceId) {
                        dispatch(clearWorkspaceState());
                    }
                    
                    // Check if secret_key exists and update if needed
                    if (response.data.has_secret_key === false) {
                        try {
                            const encryptedVaultKey = encryptKeyData(vaultKey);
                            await AuthService.updateSecretKey(encryptedVaultKey);
                            console.log('Secret key updated successfully');
                        } catch (secretKeyError) {
                            console.error('Failed to update secret key:', secretKeyError);
                            // Continue with login process even if secret key update fails
                        }
                    }

                    // Notify extension of login with all auth data
                    try {
                        await notifyExtensionOfLogin({
                            isAuthenticated: true,
                            user: initialAuthData.user,
                            role_id: initialAuthData.role_id,
                            permissions: initialAuthData.permissions,
                            team_id: initialAuthData.team_id,
                            workspaceId: response.data.activeWorkspaceId,
                            encryptionKey: encryptionKey,
                            symmetricKey: symmetricKey,
                            vaultKey: vaultKey
                        });
                        console.log('Web: Extension notified of login success');
                    } catch (error) {
                        console.error('Failed to sync with extension, but login successful:', error);
                    }
                    
                    // Return success with crypto version info for migration prompt
                    return { 
                        success: true,
                        cryptoVersion: cryptoVersion,
                        showMigrationPrompt: cryptoVersion === 1 // Show prompt for old users
                    };
                } catch (decryptError) {
                    console.error('Decryption error:', decryptError);
                    return {
                        success: false,
                        error: 'Failed to decrypt vault key'
                    };
                }
            } else {
                return {
                    success: false,
                    error: response.message || 'Login failed',
                    statusCode: response.statusCode,
                    // Pass through security-related fields
                    accountLocked: response.accountLocked,
                    securityAlert: response.securityAlert,
                    failedAttempts: response.failedAttempts
                };
            }
        } catch (err: any) {
            console.error('Login error:', err);
            return {
                success: false,
                error: err.message || 'An error occurred during login'
            };
        }
    };

    const register = async (credentials: IRegisterCredentials) => {
        try {
            const response = await AuthService.register(credentials);
            
            if (response.success) {
                // return await login({
                //     email: credentials.email,
                //     password: credentials.password
                // });
            }
            return false;
        } catch (error) {
            console.error('Registration error:', error);
            return false;
        }
    };

    const logoutUser = async () => {
        try {
            // Clear all secure keys
            secureStore.clearKeys();
            
            // Clear Redux state
            dispatch(logout());
            
            // Explicitly clear localStorage to ensure no persisted state remains
            localStorage.removeItem('persist:root');
            
            // Clear any session storage items
            sessionStorage.clear();
            
            // For extra safety, clear any specific workspace-related items
            localStorage.removeItem('activeWorkspace');
            localStorage.removeItem('workspaces');
            
            // Notify extension of logout
            try {
                await notifyExtensionOfLogout();
                console.log('Web: Extension notified of logout');
            } catch (error) {
                console.error('Error notifying extension of logout:', error);
            }
            
            // Call the original AuthService logout method to maintain existing functionality
            AuthService.logout();
            
            return { success: true };
        } catch (error) {
            console.error('Logout error:', error);
            return { success: false, error };
        }
    };

    const verifyMasterPassword = async (password: string): Promise<boolean> => {
        try {
            // Get email from secure store
            if (!user) {
                throw new Error('No user found in secure store');
            }

            // Derive auth key using the same process as login
            const { authKey } = await deriveKeys(password, user.email);

            const response = await fetch(`${config.API_URL}/export/verify-master-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ authKey }),
                credentials: 'include', // Send cookies for session auth
            });

            const data = await response.json();
            
            if (!data.success) {
                throw new Error(data.message);
            }

            return data.isValid;
        } catch (error) {
            console.error('Password verification failed:', error);
            return false;
        }
    };

    return {
        user,
        isAuthenticated,
        loading,
        error,
        login,
        register,
        logout: logoutUser,
        verifyMasterPassword,
        role_id,
        permissions,
        team_id
    };
};